import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { useSidebar } from '../context/SidebarContext';
import {
  FiHome,
  FiUsers,
  FiMenu,
  FiShoppingCart,
  FiMonitor,
  FiPrinter,
  FiMessageSquare,
  FiGrid,
  FiX,
  FiMenu as FiMenuIcon,
  FiCalendar,
  FiClock,
  FiStar,
  FiSettings,
  FiBriefcase,
  FiShoppingBag,
  FiTrendingUp,
  FiChevronLeft,
  FiChevronRight,
  FiBell,
  FiPackage,
  FiDollarSign
} from 'react-icons/fi';
import ClockComponent from 'react-clock';
import 'react-clock/dist/Clock.css';
import {
  isLocalHubRuntime,
  LOCAL_HUB_RESTAURANT_PATHS,
  LOCAL_HUB_STAFF_PATHS,
  getLocalHubHomePath,
  filterLocalHubMenuItems,
} from '../utils/runtime';

const MODULES_CACHE_TTL_MS = 12 * 60 * 1000; // 12 minutes

function Sidebar() {
  const { user } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [viewMode, setViewMode] = useState('operations');
  const [enabledModules, setEnabledModules] = useState([]);
  const [kitchenMode, setKitchenMode] = useState('kds');
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const { isCollapsed, toggleCollapse } = useSidebar();
  const ownerTenantId = localStorage.getItem('ownerTenantId');
  const isOwner = user?.role === 'owner' || user?.is_owner;
  const [pendingNotifications, setPendingNotifications] = useState(0);

  const readModulesCache = (fetchKey) => {
    try {
      const raw = localStorage.getItem(`elydine_modules_cache:${fetchKey}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed?.enabledModules) || !parsed?.fetchedAt) return null;
      return parsed;
    } catch {
      return null;
    }
  };

  const writeModulesCache = (fetchKey, modules, mode = 'kds') => {
    try {
      localStorage.setItem(
        `elydine_modules_cache:${fetchKey}`,
        JSON.stringify({
          enabledModules: Array.isArray(modules) ? modules : [],
          kitchenMode: mode,
          fetchedAt: Date.now()
        })
      );
    } catch {
      // ignore storage errors
    }
  };

  const isActive = (path) => location.pathname === path;

  // Load view mode from localStorage on mount
  useEffect(() => {
    const savedMode = localStorage.getItem('elydine_view_mode');
    if (savedMode === 'operations' || savedMode === 'management') {
      setViewMode(savedMode);
    }
  }, []);

  // Staff: poll pending notifications (waiter calls + ready dishes)
  useEffect(() => {
    if (!user || user.role !== 'staff') {
      setPendingNotifications(0);
      return;
    }

    let isMounted = true;

    const fetchPendingNotifications = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }

      try {
        const [waiterCallsResponse, activeOrdersResponse] = await Promise.all([
          api.get('/api/waiter-calls?status=pending'),
          api.get('/api/pos/orders/active'),
        ]);

        const pendingCallsCount = waiterCallsResponse.data?.waiterCalls?.length || 0;
        const orders = activeOrdersResponse.data?.orders || [];
        let readyDishesCount = 0;

        orders.forEach((order) => {
          (order.order_items || []).forEach((item) => {
            if ((item.status || '').toLowerCase() === 'ready') {
              readyDishesCount += 1;
            }
          });
        });

        const count = pendingCallsCount + readyDishesCount;

        if (!isMounted) return;
        setPendingNotifications(count);

        // On mobile, if there are pending notifications and user is not already on Notifications,
        // keep the sidebar visible to draw attention.
        if (count > 0 && window.innerWidth < 1024 && location.pathname !== '/staff/notifications') {
          setIsMobileMenuOpen(true);
        }
      } catch (error) {
        // Fail silently; we don't want sidebar to break because of this
        if (!isMounted) return;
      }
    };

    fetchPendingNotifications();
    // Poll frequently so staff notice new notifications quickly, pausing when tab is hidden
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchPendingNotifications();
    }, 5000);

    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        fetchPendingNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, location.pathname]);

  useEffect(() => {
    let isMounted = true;

    const fetchModules = async () => {
      if (!user) return;

      if (isOwner && !ownerTenantId) {
        setEnabledModules([]);
        setModulesLoaded(true);
        return;
      }

      // Unique key for this user/tenant in both online and offline mode.
      const fetchKey = `${user?.id || ''}-${ownerTenantId || ''}`;

      // Fast path: use cached modules instantly to prevent sidebar flicker.
      const cached = readModulesCache(fetchKey);
      if (cached?.enabledModules) {
        setEnabledModules(cached.enabledModules);
        setKitchenMode(cached.kitchenMode === 'kot' ? 'kot' : 'kds');
        setModulesLoaded(true);
      }

      // Only hit API if cache is missing or stale.
      const isCacheFresh = cached?.fetchedAt && (Date.now() - cached.fetchedAt < MODULES_CACHE_TTL_MS);
      if (isCacheFresh) {
        return;
      }

      try {
        const response = await api.get('/api/tenant/modules');
        const data = response.data || {};
        const modules = data.enabledModules || [];
        const mode = String(data.kitchenMode || 'kds').toLowerCase() === 'kot' ? 'kot' : 'kds';

        if (isMounted) {
          setEnabledModules(modules);
          setKitchenMode(mode);
          setModulesLoaded(true);
        }

        writeModulesCache(fetchKey, modules, mode);
      } catch (error) {
        if (isMounted) {
          // If we already had cache, keep it. Otherwise mark load complete.
          setModulesLoaded(true);
        }
      }
    };

    fetchModules();

    // Background refresh every ~12 minutes while Sidebar is mounted.
    const interval = setInterval(fetchModules, MODULES_CACHE_TTL_MS);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [user, isOwner, ownerTenantId]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Analog Clock Component using react-clock library
  function AnalogClock() {
    const [istDate, setIstDate] = useState(new Date());

    useEffect(() => {
      // Function to get IST time as a Date object
      const updateISTTime = () => {
        const now = new Date();
        
        // Use Intl.DateTimeFormat to get IST time components
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
        
        const parts = formatter.formatToParts(now);
        const year = parseInt(parts.find(p => p.type === 'year')?.value || '0', 10);
        const month = parseInt(parts.find(p => p.type === 'month')?.value || '0', 10);
        const day = parseInt(parts.find(p => p.type === 'day')?.value || '0', 10);
        const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
        const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
        const second = parseInt(parts.find(p => p.type === 'second')?.value || '0', 10);
        
        // Create Date object with IST time values
        const istDate = new Date(year, month - 1, day, hour, minute, second);
        setIstDate(istDate);
      };

      // Update immediately on mount
      updateISTTime();
      
      // Update every second
      const timer = setInterval(updateISTTime, 1000);

      return () => clearInterval(timer);
    }, []);

    return (
      <div className="pt-3 pb-5 px-5 flex items-center justify-center">
        <div style={{ width: '140px', height: '140px' }}>
          <ClockComponent
            value={istDate}
            size={140}
            renderNumbers={false}
            renderSecondHand={false}
            hourHandWidth={3}
            minuteHandWidth={2}
            hourMarksWidth={2}
            minuteMarksWidth={0.5}
          />
        </div>
      </div>
    );
  }

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const renderSidebarContent = (menuItems, portalName, showToggle = false, options = {}) => {
    const { homePath = null, skipModuleFilter = false } = options;
    // When collapsed, show all items regardless of toggle state
    // When expanded, filter by toggle state if toggle is enabled
    const filteredItems = isCollapsed
      ? menuItems // Show all items when collapsed
      : (showToggle 
          ? (viewMode === 'operations' 
              ? menuItems.filter(item => item.section === 'operations')
              : menuItems.filter(item => item.section === 'management'))
          : menuItems);

    const moduleFilteredItems = filteredItems.filter((item) => {
      if (skipModuleFilter) return true;
      if (!item.moduleKey) return true;
      // Show items optimistically while loading - only hide if explicitly not in enabledModules
      // This prevents the flash when modules are being fetched
      if (!modulesLoaded) return true; // Show items while loading
      if (enabledModules.length === 0) return false;
      return enabledModules.includes(item.moduleKey);
    });


    return (
      <>
        <div className={`px-6 py-5 flex items-center justify-center relative transition-all ${isCollapsed ? 'px-4' : ''}`}>
          <Link to={
            homePath
            || (user?.role === 'owner' || user?.is_owner
              ? '/owner/dashboard'
              : user?.role === 'restaurant_admin'
                ? '/restaurant/dashboard'
                : '/staff/dashboard')
          } className="flex items-center justify-center w-full">
            <img 
              src="/logo-1.png" 
              alt="Elyspr" 
              className={`h-28 w-auto object-contain transition-all ${isCollapsed ? 'h-20' : ''}`}
              onError={(e) => {
                // Fallback to local svg if png is missing
                if (!e.target.src.endsWith('/logo-1.svg')) {
                  e.target.src = '/logo-1.svg';
                }
              }}
            />
          </Link>
          {/* Mobile close button */}
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden absolute right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <FiX className="text-xl text-gray-600" />
          </button>
          {/* Collapse Toggle Button - Desktop only */}
          <button
            onClick={toggleCollapse}
            className="hidden lg:flex absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <FiChevronRight className="h-4 w-4 text-gray-600" />
            ) : (
              <FiChevronLeft className="h-4 w-4 text-gray-600" />
            )}
          </button>
        </div>

        {/* View Mode Toggle - Only show if enabled and not collapsed */}
        {showToggle && !isCollapsed && (
          <div className="px-3 py-3 border-b border-gray-100">
            <div className="flex gap-1.5 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => {
                  setViewMode('operations');
                  localStorage.setItem('elydine_view_mode', 'operations');
                }}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-2.5 text-xs font-medium transition-all whitespace-nowrap ${
                  viewMode === 'operations'
                    ? 'bg-gray-800 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FiShoppingBag className="h-3.5 w-3.5 flex-shrink-0" />
                <span>Operations</span>
              </button>
              <button
                onClick={() => {
                  setViewMode('management');
                  localStorage.setItem('elydine_view_mode', 'management');
                }}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-2.5 text-xs font-medium transition-all whitespace-nowrap ${
                  viewMode === 'management'
                    ? 'bg-gray-800 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FiBriefcase className="h-3.5 w-3.5 flex-shrink-0" />
                <span>Management</span>
              </button>
            </div>
          </div>
        )}
        
        <nav className={`flex-1 py-4 space-y-1 overflow-y-auto overflow-x-hidden transition-all ${isCollapsed ? 'px-2' : 'px-4'}`}>
          {moduleFilteredItems.map((item) => {
            const Icon = item.icon;
            const isStaffNotificationsItem = user?.role === 'staff' && item.path === '/staff/notifications';
            const hasPendingNotifications = isStaffNotificationsItem && pendingNotifications > 0;
            const shouldPulseNotifications = hasPendingNotifications && location.pathname !== '/staff/notifications';

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={(e) => {
                  setIsMobileMenuOpen(false);
                  // Don't expand sidebar when clicking links
                  e.stopPropagation();
                }}
                className={`flex items-center rounded-lg py-2.5 text-sm font-medium transition-colors ${
                  isCollapsed 
                    ? 'justify-center px-2' 
                    : 'gap-3 px-3'
                } ${
                  isActive(item.path)
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                } ${
                  hasPendingNotifications ? 'border border-red-300 bg-red-50' : ''
                } ${
                  shouldPulseNotifications ? 'animate-pulse' : ''
                }`}
                title={isCollapsed ? item.label : ''}
              >
                <Icon className={`h-4 w-4 flex-shrink-0 ${hasPendingNotifications ? 'text-red-600' : ''}`} />
                {!isCollapsed && (
                  <span className="leading-tight flex-1 flex items-center justify-between">
                    <span>{item.label}</span>
                    {hasPendingNotifications && (
                      <span className="ml-2 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] min-w-[18px] h-4 px-1">
                        {pendingNotifications}
                      </span>
                    )}
                  </span>
                )}
              </Link>
            );
          })}
          
          {/* Analog Clock Widget - Right below last tab - Hidden when collapsed */}
          {!isCollapsed && (
            <div className="pt-4 mt-2">
              <AnalogClock />
            </div>
          )}
        </nav>
      </>
    );
  };

  // Owner Sidebar (default)
  if (isOwner && !ownerTenantId) {
    const menuItems = [
      { path: '/owner/dashboard', icon: FiHome, label: 'Dashboard', section: 'owner' },
    ];

    return (
      <>
        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="lg:hidden fixed top-3 sm:top-4 left-3 sm:left-4 z-50 bg-white p-2.5 sm:p-3 rounded-lg shadow-lg border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
          aria-label="Open menu"
        >
          <FiMenuIcon className="text-xl sm:text-2xl text-gray-900" />
        </button>

        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div 
          className={`
            fixed inset-y-0 left-0 z-40
            bg-gray-50 h-screen flex flex-col
            transform transition-all duration-300 ease-in-out
            ${isCollapsed ? 'w-20' : 'w-64'}
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          {renderSidebarContent(menuItems, 'Owner Portal')}
        </div>
      </>
    );
  }

  // Restaurant Admin Sidebar
  if (user?.role === 'restaurant_admin' || (isOwner && ownerTenantId)) {
    // Operations (front-facing): Daily restaurant operations
    const operationsItems = [
      { path: '/restaurant/dashboard', icon: FiHome, label: 'Dashboard', section: 'operations' },
      { path: '/restaurant/analytics', icon: FiTrendingUp, label: 'Analytics', section: 'operations', moduleKey: 'analytics' },
      { path: '/restaurant/pos', icon: FiShoppingCart, label: 'POS', section: 'operations', moduleKey: 'pos' },
      { path: '/restaurant/kds', icon: FiMonitor, label: kitchenMode === 'kot' ? 'KOT' : 'KDS', section: 'operations', moduleKey: 'kds' },
      { path: '/restaurant/bills', icon: FiPrinter, label: 'Bills', section: 'operations', moduleKey: 'bills' },
      { path: '/restaurant/tables', icon: FiGrid, label: 'Tables', section: 'operations', moduleKey: 'pos' },
      { path: '/restaurant/reservations', icon: FiCalendar, label: 'Reservations', section: 'operations', moduleKey: 'reservations' },
      { path: '/restaurant/waitlist', icon: FiClock, label: 'Waitlist', section: 'operations', moduleKey: 'waitlist' },
    ];

    // Management (back office): Configuration and management
    const managementItems = [
      { path: '/restaurant/menu', icon: FiMenu, label: 'Menu', section: 'management' },
      { path: '/restaurant/inventory', icon: FiPackage, label: 'Inventory', section: 'management', moduleKey: 'inventory' },
      { path: '/restaurant/accounting', icon: FiDollarSign, label: 'Accounting', section: 'management', moduleKey: 'accounting' },
      { path: '/restaurant/guests', icon: FiUsers, label: 'Guests', section: 'management' },
      { path: '/restaurant/feedback', icon: FiStar, label: 'Feedback', section: 'management', moduleKey: 'feedback' },
      { path: '/restaurant/broadcast', icon: FiMessageSquare, label: 'Broadcast', section: 'management', moduleKey: 'broadcast' },
      { path: '/restaurant/whatsapp-menu', icon: FiMessageSquare, label: 'WhatsApp Menu', section: 'management', moduleKey: 'whatsapp_menu' },
      { path: '/restaurant/settings', icon: FiSettings, label: 'Settings', section: 'management', moduleKey: 'settings' },
      { path: '/restaurant/staff', icon: FiUsers, label: 'Staff Management', section: 'management', moduleKey: 'staff_portal' },
      { path: '/restaurant/staff-performance', icon: FiTrendingUp, label: 'Staff Performance', section: 'management', moduleKey: 'staff_performance' },
    ];

    const menuItems = [...operationsItems, ...managementItems];
    const isLocalHub = isLocalHubRuntime();
    const visibleMenuItems = isLocalHub
      ? filterLocalHubMenuItems(menuItems, LOCAL_HUB_RESTAURANT_PATHS)
      : menuItems;

    return (
      <>
        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="lg:hidden fixed top-3 sm:top-4 left-3 sm:left-4 z-50 bg-white p-2.5 sm:p-3 rounded-lg shadow-lg border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
          aria-label="Open menu"
        >
          <FiMenuIcon className="text-xl sm:text-2xl text-gray-900" />
        </button>

        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div 
          className={`
            fixed inset-y-0 left-0 z-40
            bg-gray-50 h-screen flex flex-col
            transform transition-all duration-300 ease-in-out
            ${isCollapsed ? 'w-20' : 'w-64'}
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          {renderSidebarContent(visibleMenuItems, 'Restaurant Portal', !isLocalHub, {
            homePath: isLocalHub ? getLocalHubHomePath('restaurant_admin') : null,
            skipModuleFilter: isLocalHub,
          })}
        </div>
      </>
    );
  }

  // Staff Sidebar
  if (user?.role === 'staff') {
    const menuItems = [
      // POS should be the default/first tab for staff
      { path: '/staff/orders', icon: FiShoppingCart, label: 'POS', moduleKey: 'pos' },
      { path: '/staff/kds', icon: FiMonitor, label: kitchenMode === 'kot' ? 'KOT' : 'KDS', moduleKey: 'kds' },
      { path: '/staff/notifications', icon: FiBell, label: 'Notifications' },
    ];

    const isLocalHub = isLocalHubRuntime();
    const visibleMenuItems = isLocalHub
      ? filterLocalHubMenuItems(menuItems, LOCAL_HUB_STAFF_PATHS)
      : menuItems;

    return (
      <>
        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="lg:hidden fixed top-3 sm:top-4 left-3 sm:left-4 z-50 bg-white p-2.5 sm:p-3 rounded-lg shadow-lg border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
          aria-label="Open menu"
        >
          <FiMenuIcon className="text-xl sm:text-2xl text-gray-900" />
        </button>

        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div 
          className={`
            fixed inset-y-0 left-0 z-40
            bg-gray-50 h-screen flex flex-col
            transform transition-all duration-300 ease-in-out
            ${isCollapsed ? 'w-20' : 'w-64'}
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          {renderSidebarContent(visibleMenuItems, 'Staff Portal', false, {
            homePath: isLocalHub ? getLocalHubHomePath('staff') : null,
            skipModuleFilter: isLocalHub,
          })}
        </div>
      </>
    );
  }

  return null;
}

export default Sidebar;

